import classNames from 'classnames'
import { useEffect, useId, useState } from 'react'
import {
  Link,
  Params,
  useMatches,
  useParams,
  useSearchParams,
} from 'react-router'
import { UserType } from '~/api/__generated-types__'
import { useAuth } from '~/auth/useAuth'
import { ROUTES } from '~/routes-typed'
import { cleanInt } from '~/utils/helpers'
import { focusRing } from '~/utils/styles'

import {
  JOVIVA_SERVICE_ICONS,
  JOVIVA_SERVICE_TITLES,
  JovivaService,
  JovivaServiceKey,
} from './constants'
import { type DuoColorIconProps } from './icons'

interface ActiveJovivaService {
  key: JovivaServiceKey
  title: string
  icon: React.FC<DuoColorIconProps>
  getUrl: (params: Params, searchParams: URLSearchParams) => string
  matchPatterns: string[]
}

/**
 * Filters Joviva services based on deactivated features and user permissions
 */
const filterJovivaServices = (
  services: ActiveJovivaService[],
  userInfo: UserType | undefined,
): ActiveJovivaService[] => {
  // If the user is an RSR, deactivate all services
  if (userInfo?.isRsr) {
    return []
  }
  return services.filter(
    service =>
      service.key !== 'PAGE_BUILDER' || userInfo?.isPagebuilderFeatureActive,
  )
}

/**
 * Tries to get the storeId from the URL params or search params.
 * Example
 *  - /store/42/calendar   --> 42
 *  - /calendar-settings/sync?storeId=42   --> 42
 * */
const getStoreIdFromUrl = (
  params: ReturnType<typeof useParams>,
  searchParams: URLSearchParams,
): number | undefined => {
  let storeId: number | undefined = undefined
  if (typeof params !== 'string' && 'storeId' in params && params.storeId) {
    storeId = parseInt(params.storeId)
  }
  if (!storeId) {
    storeId = cleanInt(searchParams.get('storeId'))
  }
  return storeId
}

export const MINI_NAV_JOVIVA_SERVICES: ActiveJovivaService[] = [
  {
    key: JovivaService.CALENDAR,
    title: JOVIVA_SERVICE_TITLES['CALENDAR'],
    icon: JOVIVA_SERVICE_ICONS['CALENDAR'],
    getUrl: (params, searchParams) => {
      const storeId = getStoreIdFromUrl(params, searchParams)
      return storeId
        ? ROUTES.STORE.$buildPath({
            params: { id: storeId },
          })
        : ROUTES.CHOOSE_STORE.$buildPath({})
    },
    matchPatterns: [
      'routes/choose-store',
      'routes/store.$storeId',
      'routes/calendar-settings',
      'routes/employee-settings',
    ],
  },
  {
    key: JovivaService.GOOGLE_BUSINESS,
    title: JOVIVA_SERVICE_TITLES['GOOGLE_BUSINESS'],
    icon: JOVIVA_SERVICE_ICONS['GOOGLE_BUSINESS'],
    getUrl: () => ROUTES.GOOGLE_BUSINESS_REDIRECT.$buildPath({}),
    matchPatterns: ['routes/company.$companyId.google-business'],
  },
  {
    key: JovivaService.ERP,
    title: JOVIVA_SERVICE_TITLES['ERP'],
    icon: JOVIVA_SERVICE_ICONS['ERP'],
    getUrl: () => ROUTES.ERP_REDIRECT.$buildPath({}),
    matchPatterns: ['routes/company.$companyId.erp-sync'],
  },
  {
    key: JovivaService.PAGE_BUILDER,
    title: JOVIVA_SERVICE_TITLES['PAGE_BUILDER'],
    icon: JOVIVA_SERVICE_ICONS['PAGE_BUILDER'],
    getUrl: () => ROUTES.PAGE_BUILDER.$buildPath({}),
    matchPatterns: ['routes/company.$companyId.page-builder'],
  },
]

interface ServiceLinkProps {
  service: ActiveJovivaService
  isActive: boolean
  onClick?: () => void
}

export const ServiceLink = ({
  onClick,
  service,
  isActive,
}: ServiceLinkProps) => {
  const titleId = useId()
  const params = useParams()
  const [searchParams] = useSearchParams()

  return (
    <Link
      to={service.getUrl(params, searchParams)}
      className={classNames(
        'flex h-[4.25rem] w-[4.25rem] flex-col items-center justify-center rounded-2xl bg-white transition-all hover:bg-blue-50',
        focusRing,
        // prevent outline from being cut off, in case there is no gap between the links
        '!-outline-offset-2',
      )}
      aria-labelledby={titleId}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      title={service.title} // refactor me, use some kind of tooltip
    >
      <service.icon className={'block w-[2.125rem]'} />
      <span id={titleId} className={'sr-only'}>
        {service.title}
      </span>
    </Link>
  )
}

const useActiveServiceIndex = (services: { matchPatterns: string[] }[]) => {
  const matches = useMatches()
  return services.findIndex(service =>
    matches.some(match => service.matchPatterns.includes(match.id)),
  )
}

export type JovivaServicesHeaderNavigationProps = object

export const JovivaServicesHeaderNavigation = () => {
  const activeIndex = useActiveServiceIndex(MINI_NAV_JOVIVA_SERVICES)
  const { userInfo } = useAuth()

  // In addition to `activeIndex` we need to store the state in order to give immediate ui feedback
  // for a more "app-like" experience, when the user clicks a link.
  // When the navigation and therefore the activeIndex changes, we override the state
  const [activeIndexState, setActiveIndexState] = useState<number>(activeIndex)
  useEffect(() => {
    setActiveIndexState(activeIndex)
  }, [activeIndex])

  const navItems = filterJovivaServices(MINI_NAV_JOVIVA_SERVICES, userInfo)

  return (
    <div className={'relative'}>
      <nav className={'-mb-3 flex pb-3'}>
        {navItems.map((service, index) => {
          const isActive = activeIndex === index
          return (
            <ServiceLink
              key={service.title}
              service={service}
              onClick={() => {
                setActiveIndexState(index)
              }}
              isActive={isActive}
            />
          )
        })}
      </nav>
      {activeIndexState !== -1 && (
        <span
          style={{
            transform: `translateX(${activeIndexState * 100}%)`,
          }}
          className={classNames(
            'absolute bottom-0 left-0 h-[5px] transition-all',
            navItems.length === 3 ? 'w-1/3' : 'w-1/4',
          )}
        >
          <span className='absolute top-0 right-2 bottom-0 left-2 bg-blue-500'></span>
        </span>
      )}
    </div>
  )
}

export interface JovivaServicesMobileNavigationProps {
  onClick?: () => void
}

export const JovivaServicesMobileNavigation = ({
  onClick,
}: JovivaServicesMobileNavigationProps) => {
  const { userInfo } = useAuth()
  const activeIndex = useActiveServiceIndex(MINI_NAV_JOVIVA_SERVICES)

  const navItems = filterJovivaServices(MINI_NAV_JOVIVA_SERVICES, userInfo)

  return (
    <nav className={'flex flex-wrap justify-center gap-x-2 gap-y-4'}>
      {navItems.map((service, index) => {
        const isActive = activeIndex === index
        return (
          <div
            key={service.title}
            className={classNames(
              'flex flex-col items-center gap-y-2',
              navItems.length > 3 && 'min-w-[8rem]',
            )}
          >
            <div className={classNames('relative', isActive && 'pb-3')}>
              <ServiceLink
                key={service.title}
                service={service}
                onClick={onClick}
                isActive={isActive}
              />
              {isActive && (
                <span
                  className={classNames(
                    'absolute right-0 bottom-0 left-0 h-[5px] bg-blue-500',
                  )}
                  aria-hidden
                />
              )}
            </div>
            <span
              className='text-caption1 text-center whitespace-pre'
              aria-hidden
            >
              {
                // Only show the first word, but the link contains the full title
                // in an accessible way
                service.title.split(' ')[0]
              }
            </span>
          </div>
        )
      })}
    </nav>
  )
}
